import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink, useOutletContext } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  TextField,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { palette } from '../../theme/colors';

interface CartItem {
  productId: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  minQuantity?: number;
}

interface PortalContext {
  isAuthenticated: boolean;
}

const createQuoteRequest = async (
  orgSlug: string,
  token: string,
  data: { items: Array<{ productId: string; quantity: number }>; notes?: string }
) => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/b2b-portal/${orgSlug}/quote-request`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to create quote request');
  }
  return response.json();
};

export const B2BCartPage: React.FC = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useOutletContext<PortalContext>();
  const token = localStorage.getItem('access_token');

  const [cart, setCart] = useState<CartItem[]>([]);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [quoteSuccess, setQuoteSuccess] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/b2b/${orgSlug}/login`);
      return;
    }
    const cartStr = localStorage.getItem('b2b_cart');
    if (cartStr) {
      setCart(JSON.parse(cartStr));
    }
  }, [isAuthenticated, navigate, orgSlug]);

  const updateCart = (newCart: CartItem[]) => {
    setCart(newCart);
    localStorage.setItem('b2b_cart', JSON.stringify(newCart));
  };

  const handleUpdateQuantity = (productId: string, delta: number) => {
    const newCart = cart.map((item) => {
      if (item.productId === productId) {
        const minQty = item.minQuantity || 1;
        const newQty = Math.max(minQty, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    });
    updateCart(newCart);
  };

  // Check if any item is below minimum quantity
  const hasMinimumViolations = cart.some(
    (item) => item.minQuantity && item.quantity < item.minQuantity
  );

  const handleRemoveItem = (productId: string) => {
    const newCart = cart.filter((item) => item.productId !== productId);
    updateCart(newCart);
  };

  const handleClearCart = () => {
    updateCart([]);
  };

  const quoteMutation = useMutation({
    mutationFn: (data: { items: Array<{ productId: string; quantity: number }>; notes?: string }) =>
      createQuoteRequest(orgSlug!, token!, data),
    onSuccess: () => {
      setQuoteSuccess(true);
      handleClearCart();
    },
  });

  const handleRequestQuote = () => {
    const items = cart.map((item) => ({
      productId: item.productId,
      quantity: item.quantity,
    }));
    quoteMutation.mutate({ items, notes: quoteNotes || undefined });
  };

  const getImageUrl = (path?: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${path}`;
  };

  const totalAmount = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(`/b2b/${orgSlug}/products`)}
        sx={{ mb: 3, color: palette.textSecondary }}
      >
        Continue Shopping
      </Button>

      <Typography variant="h4" fontWeight={700} gutterBottom>
        <ShoppingCartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        B2B Cart
      </Typography>

      {cart.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <ShoppingCartIcon sx={{ fontSize: 80, color: palette.textMuted, mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Your cart is empty
          </Typography>
          <Button
            component={RouterLink}
            to={`/b2b/${orgSlug}/products`}
            variant="contained"
            sx={{ mt: 2 }}
          >
            Browse Products
          </Button>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Cart Items */}
          <Box sx={{ flex: 2 }}>
            <Card>
              <CardContent>
                {cart.map((item, index) => (
                  <React.Fragment key={item.productId}>
                    {index > 0 && <Divider sx={{ my: 2 }} />}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      {/* Image */}
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: 2,
                          bgcolor: palette.sky,
                          flexShrink: 0,
                          overflow: 'hidden',
                        }}
                      >
                        {item.image ? (
                          <Box
                            component="img"
                            src={getImageUrl(item.image)}
                            alt={item.name}
                            sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <ShoppingCartIcon sx={{ color: palette.textMuted }} />
                          </Box>
                        )}
                      </Box>

                      {/* Details */}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.price.toFixed(2)} DZD each
                        </Typography>
                        {item.minQuantity && item.minQuantity > 1 && (
                          <Typography variant="caption" color="primary">
                            Min: {item.minQuantity} units
                          </Typography>
                        )}
                      </Box>

                      {/* Quantity Controls */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleUpdateQuantity(item.productId, -1)}
                          disabled={item.quantity <= (item.minQuantity || 1)}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography sx={{ minWidth: 40, textAlign: 'center' }}>
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleUpdateQuantity(item.productId, 1)}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>

                      {/* Item Total */}
                      <Typography variant="subtitle1" fontWeight={600} sx={{ minWidth: 100, textAlign: 'right' }}>
                        {(item.price * item.quantity).toFixed(2)} DZD
                      </Typography>

                      {/* Remove */}
                      <IconButton
                        onClick={() => handleRemoveItem(item.productId)}
                        sx={{ color: palette.error }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Box>
                  </React.Fragment>
                ))}
              </CardContent>
            </Card>
          </Box>

          {/* Summary */}
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Order Summary
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography color="text.secondary">Items ({totalItems})</Typography>
                  <Typography>{totalAmount.toFixed(2)} DZD</Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" fontWeight={600}>
                    Estimated Total
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color={palette.primary}>
                    {totalAmount.toFixed(2)} DZD
                  </Typography>
                </Box>

                {hasMinimumViolations && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Some items are below minimum order quantity.
                  </Alert>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<RequestQuoteIcon />}
                  onClick={() => setQuoteDialogOpen(true)}
                  disabled={hasMinimumViolations}
                  sx={{ mb: 2 }}
                >
                  Request Quote
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleClearCart}
                  sx={{ borderColor: palette.border }}
                >
                  Clear Cart
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Quote Dialog */}
      <Dialog open={quoteDialogOpen} onClose={() => !quoteSuccess && setQuoteDialogOpen(false)} maxWidth="sm" fullWidth>
        {quoteSuccess ? (
          <>
            <DialogContent sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: palette.success, mb: 2 }} />
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Quote Request Submitted!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                We'll review your request and get back to you shortly.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
              <Button
                variant="contained"
                onClick={() => {
                  setQuoteDialogOpen(false);
                  setQuoteSuccess(false);
                  setQuoteNotes('');
                  navigate(`/b2b/${orgSlug}/quotes`);
                }}
              >
                View My Quotes
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Request Quote for {totalItems} Items</DialogTitle>
            <DialogContent>
              <Box sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Estimated Total: <strong>{totalAmount.toFixed(2)} DZD</strong>
                </Typography>
                <TextField
                  label="Notes (Optional)"
                  multiline
                  rows={4}
                  fullWidth
                  value={quoteNotes}
                  onChange={(e) => setQuoteNotes(e.target.value)}
                  placeholder="Add any special requirements, delivery instructions, or questions..."
                />
              </Box>
              {quoteMutation.error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {quoteMutation.error instanceof Error ? quoteMutation.error.message : 'Failed to submit'}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setQuoteDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleRequestQuote}
                disabled={quoteMutation.isPending}
              >
                {quoteMutation.isPending ? 'Submitting...' : 'Submit Quote Request'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};
